/**
 * Enhanced Theme Provider
 * Advanced theme system with multiple theme support, persistence, and system detection
 */

import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import { ThemeContext } from "./theme-context";
import {
  THEME_STORAGE_KEY,
  THEME_VARIANT_STORAGE_KEY,
  type ResolvedTheme,
  type ThemeConfig,
  type ThemeMode,
} from "./theme-types";
import {
  isThemeMode,
  isThemeVariant,
  THEME_VARIANTS,
  type ThemeVariant,
} from "./theme-utils";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
  defaultVariant?: ThemeVariant;
  storageKey?: string;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
}

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  defaultVariant = "default",
  storageKey = THEME_STORAGE_KEY,
  enableSystem = true,
  disableTransitionOnChange = true,
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(defaultTheme);
  const [currentTheme, setCurrentThemeState] =
    useState<ThemeVariant>(defaultVariant);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>("light");
  const [isMounted, setIsMounted] = useState(false);

  // Get resolved theme based on mode and system preference
  const resolvedTheme: ResolvedTheme = mode === "system" ? systemTheme : mode;

  // System theme detection
  const getSystemTheme = useCallback((): ResolvedTheme => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }, []);

  // Update system theme when it changes
  useEffect(() => {
    if (!enableSystem) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    // Set initial system theme
    setSystemTheme(getSystemTheme());

    // Listen for system theme changes
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [enableSystem, getSystemTheme]);

  // Load persisted theme on mount
  useEffect(() => {
    try {
      const persistedMode = localStorage.getItem(storageKey);
      const persistedVariant = localStorage.getItem(THEME_VARIANT_STORAGE_KEY);

      if (isThemeMode(persistedMode)) {
        setModeState(persistedMode);
      }

      if (isThemeVariant(persistedVariant)) {
        setCurrentThemeState(persistedVariant);
      }
    } catch (error) {
      console.warn("Failed to load theme from localStorage:", error);
    }

    setIsMounted(true);
  }, [storageKey]);

  // Apply theme changes to DOM
  useEffect(() => {
    if (!isMounted) return;

    // Temporarily disable transitions to prevent flashing
    if (disableTransitionOnChange) {
      const css = document.createElement("style");
      css.type = "text/css";
      css.appendChild(
        document.createTextNode(`
        *, *::before, *::after {
          transition-duration: 0.01ms !important;
          transition-delay: 0.01ms !important;
          animation-duration: 0.01ms !important;
          animation-delay: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      `),
      );
      document.head.appendChild(css);

      // Clean up after a short delay
      const timeoutId = window.setTimeout(() => {
        document.head.removeChild(css);
      }, 1);

      return () => window.clearTimeout(timeoutId);
    }

    return undefined;
  }, [resolvedTheme, currentTheme, disableTransitionOnChange, isMounted]);

  // Apply theme classes
  useEffect(() => {
    if (!isMounted) return;

    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove("light", "dark");
    Object.keys(THEME_VARIANTS).forEach((variant) => {
      root.classList.remove(`theme-${variant}`);
    });

    // Apply current theme classes
    root.classList.add(resolvedTheme);
    root.classList.add(`theme-${currentTheme}`);

    // Set color-scheme for better browser integration
    root.style.colorScheme = resolvedTheme;

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const themeColor = resolvedTheme === "dark" ? "#272B35" : "#ffffff";
      metaThemeColor.setAttribute("content", themeColor);
    }
  }, [resolvedTheme, currentTheme, isMounted]);

  // Theme setters with persistence
  const setTheme = useCallback(
    (newMode: ThemeMode) => {
      try {
        setModeState(newMode);
        localStorage.setItem(storageKey, newMode);

        // Dispatch custom event for external listeners
        window.dispatchEvent(
          new CustomEvent("themechange", {
            detail: {
              mode: newMode,
              resolvedTheme: newMode === "system" ? systemTheme : newMode,
            },
          }),
        );
      } catch (error) {
        console.warn("Failed to save theme to localStorage:", error);
        setModeState(newMode);
      }
    },
    [storageKey, systemTheme],
  );

  const setCurrentTheme = useCallback((newVariant: string) => {
    if (!Object.keys(THEME_VARIANTS).includes(newVariant)) {
      console.warn(`Invalid theme variant: ${newVariant}`);
      return;
    }

    try {
      setCurrentThemeState(newVariant as ThemeVariant);
      localStorage.setItem(THEME_VARIANT_STORAGE_KEY, newVariant);

      // Dispatch custom event
      window.dispatchEvent(
        new CustomEvent("themevariantchange", {
          detail: { variant: newVariant },
        }),
      );
    } catch (error) {
      console.warn("Failed to save theme variant to localStorage:", error);
      setCurrentThemeState(newVariant as ThemeVariant);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    if (mode === "system") {
      setTheme(systemTheme === "dark" ? "light" : "dark");
    } else {
      setTheme(mode === "dark" ? "light" : "dark");
    }
  }, [mode, systemTheme, setTheme]);

  // Listen for toggle-theme custom event (fired by command palette)
  useEffect(() => {
    const handleToggleTheme = () => toggleTheme();
    window.addEventListener("toggle-theme", handleToggleTheme);
    return () => window.removeEventListener("toggle-theme", handleToggleTheme);
  }, [toggleTheme]);

  const value: ThemeConfig = {
    mode,
    resolvedTheme,
    systemTheme,
    setTheme,
    toggleTheme,
    themes: Object.keys(THEME_VARIANTS),
    currentTheme,
    setCurrentTheme,
  };

  // Prevent hydration mismatch by not rendering until mounted
  if (!isMounted) {
    return null;
  }

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
